import { useTranslation } from 'react-i18next';
import { PROMOTION, REQUEST } from './routeNames';
import dayjs from 'dayjs';
import arEG from 'antd/locale/ar_EG';
import faIR from 'antd/locale/fa_IR';
import { MAX_FILE_SIZE_MB } from './index';

export const FAILED_ERROR = 'Something Unexpected Happened';

export const STRONG_PASSWORD_MESSAGE =
  'Password should contain Alphanumeric with atleast 1 capital and special character';
export const INVALID_EMAIL_MESSAGE = 'Not a valid email!';
export const REQUIRED_FEILD = 'This field is required!';

export const DATE_FORMAT = 'DD-MMM, YYYY';
export const DATE_FORMAT_MASK = 'DD-MM-YYYY';
export const DATE_FORMAT_MASK_AR = 'دي دي- إم إم- واي واي واي واي';
export const DATE_FORMAT_FOR_FILTER = 'YYYY-MM-DD';
export const TIME_FORMAT = 'HH:mm'; // 'h:mm:ss A' for AM/PM format
export const TIME_FORMAT_FOR_FILTER = 'HH:mm:ss';
export const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
export const FLIGHT_TIME_FORMAT = `${DATE_FORMAT} - ${TIME_FORMAT}`;
export const TIME_FORMAT_FOR_PAYLOAD = 'YYYY-MM-DDTHH:mm:ss';
export const TIME_FORMAT_FOR_PAYLOAD_WITH_Z = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
export const DATE_FORMAT_MONTH_YEAR = 'MM-YYYY';
export const DATE_FORMAT_MONTH_YEAR_AR = 'إم إم-واي واي واي واي';
export const TIME_FORMAT_IN_HOUR_AND_MINUTES = 'hMin';

export const dateLanguagePlaceholder = {
  ar: DATE_FORMAT_MASK_AR,
  en: DATE_FORMAT_MASK,
  en_month: DATE_FORMAT_MONTH_YEAR,
  ar_month: DATE_FORMAT_MONTH_YEAR_AR,
};

export const FEEDBACK_ROUTES = {
  LIST: 'feedback',
  CREATE: 'feedback/create',
  EDIT: 'feedback/edit',
  VIEW: 'feedback/view',
} as const;

export const isDisableBeforeCurrentDate = (current: dayjs.Dayjs) => current && current < dayjs().startOf('day');

export const FREE_DOMAINS = ['gmail.com', 'hotmail.com', 'yahoo.com'];

export const Genders = (t: (key: string) => string) => {
  return [
    {
      label: t('genders.male'),
      value: 'MALE',
    },
    {
      label: t('genders.female'),
      value: 'FEMALE',
    },
    {
      label: t('genders.other'),
      value: 'OTHER',
    },
  ];
};

export const Relationships = () => {
  const { t } = useTranslation();

  return [
    {
      label: t('traveler.details.relationshipSection.relationships.parent'),
      value: 'Parent',
    },
    {
      label: t('traveler.details.relationshipSection.relationships.spouse'),
      value: 'Spouse',
    },
    {
      label: t('traveler.details.relationshipSection.relationships.sibling'),
      value: 'Sibling',
    },
    {
      label: t('traveler.details.relationshipSection.relationships.child'),
      value: 'Child',
    },
    {
      label: t('traveler.details.relationshipSection.relationships.friend'),
      value: 'Friend',
    },
    {
      label: t('traveler.details.relationshipSection.relationships.colleague'),
      value: 'Colleague',
    },
  ];
};

export const IdentityDocuments = (t: (key: string) => string) => {
  return [
    {
      label: t('traveler.IdentityDocuments.id'),
      value: 'ID',
    },
    {
      label: t('traveler.IdentityDocuments.passport'),
      value: 'PASSPORT',
    },
    {
      label: t('traveler.IdentityDocuments.visa'),
      value: 'VISA',
    },
  ];
};

export const MAX_PROFILE_PICTURE_COUNT = 1;
export const MAX_INVOICE_DOCUMENT_COUNT = 1;

export const DEFAULT_DEBOUNCE_DELAY = 500; //in milliseconds

// Google Places Autocomplete constants
export const MIN_PLACE_SEARCH_CHARACTERS = 3; // Minimum characters required before calling place search API
export const PLACE_SEARCH_DEBOUNCE_DELAY = 1000; // Debounce delay in milliseconds for place search input (1 second - industry standard)
export const GOOGLE_PLACES_API_DELAY_AFTER_EMPTY_INTERNAL = 2000; // Delay in milliseconds before calling Google Places API after empty internal response

export const DEFAULT_COUNTRY = {
  CODE: '+55',
  ISO: 'BR',
  CURRENCY_CODE: 'BRL',
  ID: 30,
} as const;

export const REGEX_PATTERNS = {
  BUDGET: /^\d+(\.\d{0,2})?$/,
  PHONE: /^\+?[1-9]\d{1,14}$/,
  EMAIL: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  NUMBER_WITH_COMMAS: /\B(?=(\d{3})+(?!\d))/g,
  NUMBERS_AND_DECIMAL: /[^\d.]/g,
} as const;

export const BUDGET_VALIDATION_MESSAGE = 'Please enter a valid amount (up to 2 decimal places)';
export const MAX_BUDGET_LENGTH = 10; // 8 digits + 2 commas as a formatter (without 4 currency notation)

export const PHONE_VALIDATION_REGEX = {
  NUMERIC_ONLY: /\D/g,
  CONSECUTIVE_SPECIAL_CHARS: /[+\-()]{2,}/,
  LEADING_ZEROS: /^[+]?\d*0{2,}/,
  INVALID_CHARS: /[^0-9+()-\s]/,
  PARENTHESES_FORMAT: /^\(?\d{1,4}\)?[-.\s]?\d+/,
} as const;

// image/webp is not supported by the backend as image type - but only allowed for default upload type
export const IMAGE_TYPE = 'image/png, image/jpeg, image/jpg, application/pdf';
export const IMAGE_TYPE_WITHOUT_PDF = 'image/png, image/jpeg, image/jpg';
export const DEFAULT_UPLOAD_TYPE = 'image/png, image/jpeg, image/jpg, image/webp, application/pdf, video/mp4';

export const IMAGE_SIZE_CALCULATION = (filesize: number) => {
  return filesize / 1024 / 1024 <= MAX_FILE_SIZE_MB;
};

export const NOT_IMPLEMENTED_SIDEMENU = [PROMOTION, REQUEST];

export const routeDisplayNames = (basePath: string, t: (key: string) => string): string => {
  const titles: Record<string, string> = {
    'team-member': t('headers.team-member'),
    customer: t('headers.customer'),
    trip: t('headers.trip'),
    agency: t('headers.agency'),
    traveler: t('headers.traveler'),
    feedback: t('headers.feedback'),
    setting: t('headers.profileSettings'),
    'change-password': t('headers.changePassword'),
    home: t('headers.home'),
    reports: t('headers.reports'),
    'banner-configuration': t('headers.banners'),
    financial: t('headers.financial'),
    notifications: t('headers.notifications'),
  };

  return titles[basePath];
};

export const isDevMode = import.meta.env.VITE_APP_MODE === 'LOCAL' || import.meta.env.VITE_APP_MODE === 'DEV';

export const getStatusFilters = (t: (key: string) => string) => {
  return [
    { text: t('status.value.active'), value: 'ACTIVE' },
    { text: t('status.value.inactive'), value: 'INACTIVE' },
  ];
};

export const enum Environment {
  LOCAL = 'LOCAL',
  DEV = 'DEV',
  QA = 'QA',
  STAGING = 'Staging',
}

export const QueryCacheTime = 1000 * 60 * 60 * 24;

export const FONTS = {
  RTL: {
    FAMILY: 'Cairo',
    URL: 'https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap',
  },
  LTR: {
    FAMILY: 'Noto Sans',
    URL: 'https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500;600;700&display=swap',
  },
} as const;

export const RTL_LANGUAGES = ['ar', 'fa'] as const;

export const LOCALES = {
  ar: arEG,
  fa: faIR,
} as const;

export const LOCALES_CODES = {
  ar: 'ar-EG',
  fa: 'fa-IR',
} as const;

export const useRTL = () => {
  const { i18n } = useTranslation();
  return RTL_LANGUAGES.includes(i18n.language as (typeof RTL_LANGUAGES)[number]);
};

export const LAST_ACTIVITY_KEY = 'lastActivity';
export const WARNING_SHOWN_KEY = 'warningShown';

export const MAX_VISIBLE_TAGS = 3;

export const REFRESH_INTERVAL = 30000;

export const CATEGORY_FILTER_OPTIONS = [
  { value: 'Delays', text: 'Delays' },
  { value: 'Wrong Seat', text: 'Wrong Seat' },
  { value: 'Poor Accommodation', text: 'Poor Accommodation' },
  { value: 'Good Service', text: 'Good Service' },
  { value: 'Great Service', text: 'Great Service' },
];
